import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  Skeleton,
  Button,
  alpha,
  Paper,
  Chip,
  Divider,
  LinearProgress,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { getRobotsGet, getRobotHistory } from '../services/robotApi';
import { 
  FiShield, 
  FiClock, 
  FiZap, 
  FiTrendingUp, 
  FiTrendingDown,
  FiActivity,
  FiCalendar,
  FiBarChart2,
  FiCpu,
  FiMapPin,
  FiUsers,
  FiX,
  FiArrowRight,
} from 'react-icons/fi';
import { 
  MdOutlineWarning,
  MdOutlineCheckCircle,
} from 'react-icons/md';

const REPORT_LIMIT = 500;
const REPORT_MAX_PAGES = 50;
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const PIE_COLORS = ['#0d9488', '#6366f1', '#f59e0b', '#ec4899', '#8b5cf6', '#0891b2', '#84cc16', '#f97316', '#14b8a6', '#a855f7'];

/** Shown in charts when history has no robot name or id */
const DEFAULT_ROBOT_DISPLAY_NAME = 'Sportbot Lite';

function getRobotLabel(row) {
  const name = row?.robotName ?? row?.robot_name;
  const id = row?.robotId ?? row?.robot_id;
  const raw = name != null && String(name).trim() !== '' ? String(name).trim() : id;
  return raw != null && String(raw).trim() !== '' ? String(raw).trim() : DEFAULT_ROBOT_DISPLAY_NAME;
}

function isCompletedDisinfection(row) {
  if (row?.status === 'completed') return true;
  if (row?.completed_object != null && row?.total_object != null && row.completed_object === row.total_object) {
    return true;
  }
  return false;
}

function rowCalendarDayKey(row) {
  const d = row?.date || row?.disinfectionStartTime || row?.startTime || row?.createdAt;
  const parts = getDateParts(d);
  if (!parts) return null;
  return `${parts.year}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`;
}

function rowMonthKey(row) {
  const day = rowCalendarDayKey(row);
  return day ? day.slice(0, 7) : null;
}

function rowYearKey(row) {
  const day = rowCalendarDayKey(row);
  return day ? day.slice(0, 4) : null;
}

// Parse date from API without timezone shifting (e.g. 2026-02-04T00:00:00.000+00:00 -> Feb 2026)
function getDateParts(d) {
  if (d == null || d === '') return null;
  let s = typeof d === 'string' ? d.trim() : String(d);
  if (typeof d === 'number' && !Number.isNaN(d)) {
    const date = new Date(d);
    if (isNaN(date.getTime())) return null;
    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate(),
    };
  }
  // ISO with T: 2026-02-04T00:00:00.000+00:00 – use date part only (no timezone shift)
  if (s.includes('T')) {
    const datePart = s.split('T')[0].trim();
    const parts = datePart.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (parts) {
      return {
        year: parseInt(parts[1], 10),
        month: parseInt(parts[2], 10),
        day: parseInt(parts[3], 10),
      };
    }
  }
  const isoMatch = s.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return {
      year: parseInt(isoMatch[1], 10),
      month: parseInt(isoMatch[2], 10),
      day: parseInt(isoMatch[3], 10),
    };
  }
  const date = new Date(d);
  if (isNaN(date.getTime())) return null;
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
  };
}

// ==================== INTERNAL CSS STYLES ====================
const styles = {
  // Main container
  pageContainer: {
    minHeight: '100vh',
    background: 'linear-gradient(145deg, #f8fafc 0%, #f1f5f9 100%)',
    padding: '32px 0',
    position: 'relative',
    overflow: 'hidden',
  },

  // Background decorative elements
  bgGradient: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 20% 20%, rgba(99, 102, 241, 0.03) 0%, transparent 40%), radial-gradient(circle at 80% 80%, rgba(13, 148, 136, 0.03) 0%, transparent 40%)',
    pointerEvents: 'none',
    zIndex: 0,
  },

  bgOrb1: {
    position: 'absolute',
    top: '5%',
    right: '5%',
    width: '400px',
    height: '400px',
    background: 'radial-gradient(circle, rgba(99, 102, 241, 0.08) 0%, rgba(99, 102, 241, 0.02) 50%, transparent 70%)',
    borderRadius: '50%',
    filter: 'blur(60px)',
    pointerEvents: 'none',
    zIndex: 0,
  },

  bgOrb2: {
    position: 'absolute',
    bottom: '5%',
    left: '5%',
    width: '500px',
    height: '500px',
    background: 'radial-gradient(circle, rgba(13, 148, 136, 0.08) 0%, rgba(13, 148, 136, 0.02) 50%, transparent 70%)',
    borderRadius: '50%',
    filter: 'blur(80px)',
    pointerEvents: 'none',
    zIndex: 0,
  },

  // Content container
  contentContainer: {
    maxWidth: '1600px',
    margin: '0 auto',
    padding: '0 24px',
    position: 'relative',
    zIndex: 1,
  },

  // Header styles
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '32px',
    padding: '24px 0 8px',
  },

  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
  },

  headerIcon: {
    width: '64px',
    height: '64px',
    borderRadius: '20px',
    background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 10px 25px -5px rgba(13, 148, 136, 0.3)',
    color: '#ffffff',
    fontSize: '32px',
  },

  headerTitle: {
    fontWeight: 800,
    fontSize: '2rem',
    background: 'linear-gradient(135deg, #0f172a 0%, #334155 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    lineHeight: 1.2,
  },

  headerSubtitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#64748b',
    marginTop: '4px',
  },

  headerStats: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '8px 16px',
    background: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(10px)',
    borderRadius: '100px',
    border: '1px solid rgba(226, 232, 240, 0.8)',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
  },

  headerStatItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px 12px',
    borderRadius: '100px',
    background: alpha('#f1f5f9', 0.5),
  },

  // Stat cards
  statsGrid: {
    marginBottom: '24px',
  },

  statCard: {
    height: '100%',
    borderRadius: '24px',
    background: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(226, 232, 240, 0.8)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      borderColor: alpha('#0d9488', 0.3),
    },
  },

  statCardContent: {
    padding: '24px',
  },

  statHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },

  statIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
  },

  statTrend: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    borderRadius: '100px',
    fontSize: '0.75rem',
    fontWeight: 600,
  },

  statTrendUp: {
    background: alpha('#10b981', 0.1),
    color: '#059669',
  },

  statTrendDown: {
    background: alpha('#ef4444', 0.1),
    color: '#dc2626',
  },

  statValue: {
    fontWeight: 700,
    fontSize: '2.25rem',
    lineHeight: 1.2,
    marginBottom: '4px',
    color: '#0f172a',
  },

  statLabel: {
    color: '#64748b',
    fontSize: '0.875rem',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },

  // Chart cards
  chartCard: {
    height: '100%',
    borderRadius: '24px',
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
    transition: 'all 0.2s ease',
    '&:hover': {
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.05), 0 10px 10px -5px rgba(0, 0, 0, 0.02)',
    },
  },

  chartCardContent: {
    padding: '24px',
  },

  chartHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '20px',
  },

  chartTitle: {
    fontWeight: 700,
    fontSize: '1.125rem',
    color: '#0f172a',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },

  chartRangeToggle: {
    '& .MuiButton-root': {
      textTransform: 'none',
      fontWeight: 600,
      padding: '6px 16px',
      borderRadius: '100px',
      fontSize: '0.875rem',
      transition: 'all 0.2s ease',
    },
    '& .MuiButton-contained': {
      background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
      boxShadow: '0 4px 6px -1px rgba(13, 148, 136, 0.3)',
      color: '#ffffff',
      '&:hover': {
        background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)',
        transform: 'translateY(-1px)',
      },
    },
    '& .MuiButton-outlined': {
      borderColor: '#e2e8f0',
      color: '#475569',
      background: '#ffffff',
      '&:hover': {
        background: '#f8fafc',
        borderColor: '#94a3b8',
      },
    },
  },

  chartTooltip: {
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    padding: '12px 16px',
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
  },

  chartEmpty: {
    height: '300px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: alpha('#f8fafc', 0.5),
    borderRadius: '16px',
    padding: '32px',
    border: '2px dashed #e2e8f0',
    color: '#94a3b8',
  },

  // Summary cards
  summaryCard: {
    borderRadius: '20px',
    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
    border: '1px solid rgba(226, 232, 240, 0.8)',
    height: '100%',
    transition: 'all 0.3s ease',
    '&:hover': {
      borderColor: alpha('#0d9488', 0.5),
      transform: 'scale(1.02)',
    },
  },

  summaryCardContent: {
    padding: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
  },

  summaryText: {
    flex: 1,
    minWidth: 0,
  },

  summaryImage: {
    width: '80px',
    height: '80px',
    objectFit: 'contain',
    borderRadius: '16px',
    transition: 'transform 0.3s ease',
  },

  // Loading skeleton
  loadingSkeleton: {
    background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
    '@keyframes shimmer': {
      '0%': { backgroundPosition: '200% 0' },
      '100%': { backgroundPosition: '-200% 0' },
    },
  },
};

// ==================== COMPONENTS ====================

const StatCard = ({ icon, value, label, trend, loading, accentColor, subValue }) => (
  <Card sx={styles.statCard}>
    <CardContent sx={styles.statCardContent}>
      <Box sx={styles.statHeader}>
        <Box sx={{
          ...styles.statIcon,
          background: `linear-gradient(135deg, ${alpha(accentColor, 0.1)} 0%, ${alpha(accentColor, 0.2)} 100%)`,
          color: accentColor,
        }}>
          {icon}
        </Box>
        {trend != null && (
          <Typography variant="caption" sx={{
            ...styles.statTrend,
            ...(trend >= 0 ? styles.statTrendUp : styles.statTrendDown),
          }}>
            {trend >= 0 ? <FiTrendingUp size={12} /> : <FiTrendingDown size={12} />}
            {Math.abs(trend)}%
          </Typography>
        )}
      </Box>
      
      {loading ? (
        <>
          <Skeleton variant="text" width="80%" height={48} sx={{ borderRadius: 1, mb: 0.5 }} />
          <Skeleton variant="text" width="60%" height={20} />
        </>
      ) : (
        <>
          <Typography variant="h4" sx={styles.statValue}>
            {value != null ? value : '—'}
          </Typography>
          <Typography variant="body2" sx={styles.statLabel}>
            {subValue && (
              <Box component="span" sx={{ color: accentColor, fontWeight: 600, mr: 0.5 }}>
                {subValue}
              </Box>
            )}
            {label}
          </Typography>
        </>
      )}
    </CardContent>
  </Card>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  
  return (
    <Paper elevation={0} sx={styles.chartTooltip}>
      <Typography variant="caption" sx={{ fontWeight: 600, color: '#0f172a', display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: '#0d9488', fontWeight: 600 }}>
        {d?.count ?? 0} disinfections
      </Typography>
      {d?.change != null && d.change !== 0 && (
        <Typography variant="caption" sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          mt: 0.5,
          color: d.trend === 'up' ? '#059669' : '#dc2626',
        }}>
          {d.trend === 'up' ? <FiTrendingUp size={12} /> : <FiTrendingDown size={12} />}
          {d.change > 0 ? '+' : ''}{d.change} vs previous
        </Typography>
      )}
    </Paper>
  );
};

const PieSliceTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0]?.payload;
  if (!p) return null;
  return (
    <Paper elevation={0} sx={styles.chartTooltip}>
      <Typography variant="caption" sx={{ fontWeight: 600, color: '#0f172a', display: 'block', mb: 0.5 }}>
        {p.name}
      </Typography>
      <Typography variant="body2" sx={{ color: '#0d9488', fontWeight: 600 }}>
        {p.count} completed ({p.percentage}%)
      </Typography>
    </Paper>
  );
};

// ==================== MAIN COMPONENT ====================

export default function DisinfectionAnalysis() {
  const navigate = useNavigate();
  const [robotCount, setRobotCount] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingRobots, setLoadingRobots] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [error, setError] = useState(null);
  const [chartRange, setChartRange] = useState('Day');
  const [overviewRange, setOverviewRange] = useState('Week'); // 'Week' | 'Month' | 'Year'
  const [piePeriodKind, setPiePeriodKind] = useState('day'); // 'day' | 'month' | 'year'
  const [pieDay, setPieDay] = useState(() =>
    new Date().toLocaleDateString('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' })
  );
  const [pieMonth, setPieMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [pieYear, setPieYear] = useState(() => String(new Date().getFullYear()));
  const [overallCompletionDialogOpen, setOverallCompletionDialogOpen] = useState(false);
  const [periodPieDialogOpen, setPeriodPieDialogOpen] = useState(false);
  const [overviewDialogOpen, setOverviewDialogOpen] = useState(false);

  // Fetch robots count
  useEffect(() => {
    const fetchRobots = async () => {
      try {
        setLoadingRobots(true);
        const res = await getRobotsGet();
        const data = res.data;
        const count = data?.total ?? data?.count ?? (Array.isArray(data) ? data.length : null);
        setRobotCount(typeof count === 'number' ? count : (Array.isArray(data) ? data.length : 0));
      } catch (err) {
        console.error('Error fetching robots:', err);
        setRobotCount(0);
      } finally {
        setLoadingRobots(false);
      }
    };
    fetchRobots();
  }, []);

  // Fetch history data – request enough to get all records (backend may cap at 100 per request, so we paginate)
  useEffect(() => {
    const getRowsFromResponse = (res) => {
      const raw = res?.data;
      if (Array.isArray(raw)) return raw;
      if (raw?.data && Array.isArray(raw.data)) return raw.data;
      if (raw?.history && Array.isArray(raw.history)) return raw.history;
      return [];
    };

    const fetchHistory = async () => {
      try {
        setLoadingHistory(true);
        setError(null);
        const allRows = [];
        let page = 1;
        const seenIds = new Set();

        while (page <= REPORT_MAX_PAGES) {
          const requestedLimit = page === 1 ? 10000 : REPORT_LIMIT;
          const res = await getRobotHistory({
            limit: requestedLimit,
            page,
          });
          const rows = getRowsFromResponse(res);
          if (rows.length === 0) break;
          let newCount = 0;
          for (const row of rows) {
            const id = row._id ?? row.id ?? (row.robotId || '') + (row.date || row.disinfectionStartTime || '');
            if (!seenIds.has(id)) {
              seenIds.add(id);
              allRows.push(row);
              newCount += 1;
            }
          }
          if (newCount === 0) break;
          if (rows.length < requestedLimit) break;
          page += 1;
        }

        setHistory(allRows);
      } catch (err) {
        console.error('Error fetching robot history:', err);
        setError(err.response?.data?.message || err.message || 'Failed to load history.');
        setHistory([]);
      } finally {
        setLoadingHistory(false);
      }
    };
    fetchHistory();
  }, []);

  // Compute statistics
  const stats = useMemo(() => {
    if (!Array.isArray(history) || history.length === 0) {
      return {
        totalDisinfection: 0,
        totalRobotRunHours: 0,
        totalLightOnHours: 0,
        avgCycleTime: 0,
        successRate: 0,
        trendDisinfection: null,
        trendRunHours: null,
        trendLightHours: null,
        dailyData: [],
        weeklyData: [],
        monthlyData: [],
        weekBarData: [],
        monthOverviewData: [],
        yearOverviewData: [],
        locationData: [],
        uvEfficiency: [],
        statusCounts: { AutoDisinfection: 0, partial: 0, completed: 0 },
      };
    }

    let totalRunSeconds = 0;
    let totalLightSeconds = 0;
    let totalCycles = history.length;
    let successfulCycles = 0;
    
    const dayCounts = new Map();
    const weekCounts = new Map();
    const monthCounts = new Map();
    const yearCounts = new Map();
    const locationCounts = new Map();
    const statusCounts = { AutoDisinfection: 0, partial: 0, completed: 0 };

    history.forEach((row) => {
      const mode = (row.mode || '').toString().trim();
      const modeNorm = mode.toLowerCase().replace(/\s/g, '');
      if (modeNorm === 'autodisinfection') statusCounts.AutoDisinfection += 1;

      const status = (row.status || row.disinfectionStatus || '').toString().trim();
      const statusNorm = status.toLowerCase().replace(/\s/g, '');
      if (statusNorm === 'partial') statusCounts.partial += 1;
      else if (statusNorm === 'completed') statusCounts.completed += 1;

      if (row.status === 'completed' || row.completed_object === row.total_object) {
        successfulCycles++;
      }

      // Track locations
      if (row.location || row.subLocation) {
        const loc = row.location || row.subLocation;
        locationCounts.set(loc, (locationCounts.get(loc) || 0) + 1);
      }

      const d = row.date || row.disinfectionStartTime || row.startTime || row.createdAt;
      const parts = getDateParts(d);
      if (!parts) return;

      const dayKey = `${parts.year}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`;
      const monthKey = `${parts.year}-${String(parts.month).padStart(2, '0')}`;
      const yearKey = String(parts.year);
      const weekKey = `${parts.year}-W${String(Math.ceil(parts.day / 7)).padStart(2, '0')}`;

      dayCounts.set(dayKey, (dayCounts.get(dayKey) || 0) + 1);
      weekCounts.set(weekKey, (weekCounts.get(weekKey) || 0) + 1);
      monthCounts.set(monthKey, (monthCounts.get(monthKey) || 0) + 1);
      yearCounts.set(yearKey, (yearCounts.get(yearKey) || 0) + 1);

      const lightSec = Number(row.uvLightUsageInSeconds ?? row.disinfectionTimeTakenSeconds ?? 0);
      const runSec = Number(row.disinfectionTimeTakenSeconds ?? row.uvLightUsageInSeconds ?? 0);
      totalRunSeconds += runSec;
      totalLightSeconds += lightSec;
    });

    // Calculate trends
    const sortedDays = [...dayCounts.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    const last7 = sortedDays.slice(-7);
    const prev7 = sortedDays.slice(-14, -7);
    const last7Sum = last7.reduce((s, [, c]) => s + c, 0);
    const prev7Sum = prev7.reduce((s, [, c]) => s + c, 0);
    const trendDisinfection = prev7Sum > 0 ? Math.round(((last7Sum - prev7Sum) / prev7Sum) * 100) : null;

    // Format daily data
    const dailyData = [...dayCounts.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, count]) => ({ date, count, name: date }));

    // Format week data
    const now = new Date();
    const weekBarData = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const dayKey = d.toLocaleDateString('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' });
      weekBarData.push({
        day: DAYS[d.getDay()],
        count: dayCounts.get(dayKey) || 0,
        fullDate: dayKey,
      });
    }

    // Location data for pie chart
    const locationData = [...locationCounts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, value]) => ({ name, value }));

    // Month overview: every month from Jan (of first year with data) through current month; Jan/Feb etc. show as 0 if no data
    const monthlySorted = [...monthCounts.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    const firstMonthKey = monthlySorted.length > 0 ? monthlySorted[0][0] : null;
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    const endMonthKey = `${currentYear}-${String(currentMonth).padStart(2, '0')}`;

    const monthOverviewData = [];
    if (firstMonthKey) {
      const startY = Number(String(firstMonthKey).split('-')[0]);
      let y = startY;
      let m = 1;
      while (true) {
        const monthKey = `${y}-${String(m).padStart(2, '0')}`;
        const count = monthCounts.get(monthKey) || 0;
        const label = `${MONTH_NAMES[m - 1]} '${String(y).slice(-2)}`;
        monthOverviewData.push({ month: label, count, name: label, fullDate: monthKey });
        if (monthKey === endMonthKey) break;
        m += 1;
        if (m > 12) {
          m = 1;
          y += 1;
        }
      }
    }

    // Year overview: every year from first (when disinfection started) through current year; show 0 for years with no data (same method as month)
    const yearsSorted = [...yearCounts.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    const firstYear = yearsSorted.length > 0 ? Number(yearsSorted[0][0]) : currentYear;
    const yearOverviewData = [];
    for (let y = firstYear; y <= currentYear; y++) {
      const yearKey = String(y);
      const count = yearCounts.get(yearKey) || 0;
      yearOverviewData.push({
        year: yearKey,
        count,
        name: yearKey,
        fullDate: yearKey,
      });
    }

    return {
      totalDisinfection: totalCycles,
      totalRobotRunHours: Math.round((totalRunSeconds / 3600) * 10) / 10,
      totalLightOnHours: Math.round((totalLightSeconds / 3600) * 10) / 10,
      avgCycleTime: totalCycles > 0 ? Math.round((totalRunSeconds / totalCycles / 60) * 10) / 10 : 0,
      successRate: totalCycles > 0 ? Math.round((successfulCycles / totalCycles) * 100) : 0,
      trendDisinfection,
      dailyData,
      weeklyData: [...weekCounts.entries()]
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([date, count]) => ({ date, count, name: date })),
      monthlyData: [...monthCounts.entries()]
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([date, count]) => ({ date, count, name: date })),
      weekBarData,
      monthOverviewData,
      yearOverviewData,
      locationData,
      statusCounts,
    };
  }, [history]);

  const loading = loadingHistory;

  // Prepare chart data
  const chartData = useMemo(() => {
    const source =
      chartRange === 'Month'
        ? stats.monthlyData
        : chartRange === 'Week'
        ? stats.weeklyData
        : stats.dailyData;
    
    if (!source || source.length === 0) return [];
    
    return source.map((d, i) => {
      const prev = source[i - 1];
      const change = prev != null ? d.count - prev.count : 0;
      return { ...d, change, trend: change > 0 ? 'up' : change < 0 ? 'down' : 'same' };
    });
  }, [chartRange, stats.dailyData, stats.weeklyData, stats.monthlyData]);

  const historyDateBounds = useMemo(() => {
    let min = null;
    let max = null;
    if (!Array.isArray(history)) return { min: null, max: null, minMonth: null, maxMonth: null, minYear: null, maxYear: null };
    history.forEach((row) => {
      const key = rowCalendarDayKey(row);
      if (!key) return;
      if (min == null || key < min) min = key;
      if (max == null || key > max) max = key;
    });
    const minMonth = min ? min.slice(0, 7) : null;
    const maxMonth = max ? max.slice(0, 7) : null;
    const minYear = min ? min.slice(0, 4) : null;
    const maxYear = max ? max.slice(0, 4) : null;
    return { min, max, minMonth, maxMonth, minYear, maxYear };
  }, [history]);

  const pieYearOptions = useMemo(() => {
    const cy = new Date().getFullYear();
    let from = historyDateBounds.minYear ? parseInt(historyDateBounds.minYear, 10) : cy - 5;
    let to = historyDateBounds.maxYear ? parseInt(historyDateBounds.maxYear, 10) : cy + 1;
    if (Number.isNaN(from)) from = cy - 5;
    if (Number.isNaN(to)) to = cy + 1;
    if (from > to) [from, to] = [to, from];
    const years = [];
    for (let y = from; y <= to; y++) years.push(String(y));
    return years.length ? years : [String(cy)];
  }, [historyDateBounds.minYear, historyDateBounds.maxYear]);

  useEffect(() => {
    if (!pieYearOptions.length) return;
    if (!pieYearOptions.includes(pieYear)) {
      setPieYear(pieYearOptions[pieYearOptions.length - 1]);
    }
  }, [pieYearOptions, pieYear]);

  const robotCompletedPieData = useMemo(() => {
    const counts = new Map();
    if (!Array.isArray(history)) return [];
    history.forEach((row) => {
      if (!isCompletedDisinfection(row)) return;
      const label = getRobotLabel(row);
      counts.set(label, (counts.get(label) || 0) + 1);
    });
    const total = [...counts.values()].reduce((a, b) => a + b, 0);
    if (total === 0) return [];
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([name, value]) => ({
        name,
        value,
        count: value,
        percentage: Math.round((value / total) * 1000) / 10,
      }));
  }, [history]);

  const periodRobotPieData = useMemo(() => {
    const counts = new Map();
    if (!Array.isArray(history)) return [];
    history.forEach((row) => {
      if (!isCompletedDisinfection(row)) return;
      let inRange = false;
      if (piePeriodKind === 'day') {
        inRange = pieDay && rowCalendarDayKey(row) === pieDay;
      } else if (piePeriodKind === 'month') {
        const mk = rowMonthKey(row);
        inRange = pieMonth && mk === pieMonth;
      } else {
        const yk = rowYearKey(row);
        inRange = pieYear && yk === pieYear;
      }
      if (!inRange) return;
      const label = getRobotLabel(row);
      counts.set(label, (counts.get(label) || 0) + 1);
    });
    const total = [...counts.values()].reduce((a, b) => a + b, 0);
    if (total === 0) return [];
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([name, value]) => ({
        name,
        value,
        count: value,
        percentage: Math.round((value / total) * 1000) / 10,
      }));
  }, [history, piePeriodKind, pieDay, pieMonth, pieYear]);

  return (
    <Box sx={styles.pageContainer}>
      {/* Background decorative elements */}
      <Box sx={styles.bgGradient} />
      <Box sx={styles.bgOrb1} />
      <Box sx={styles.bgOrb2} />

      <Container maxWidth="xl" sx={styles.contentContainer}>
        {/* Header */}
        <Box sx={styles.header}>
          <Box sx={styles.headerLeft}>
            <Box sx={styles.headerIcon}>
              <FiZap size={32} />
            </Box>
            <Box>
              <Typography variant="h4" sx={styles.headerTitle}>
                UV Disinfection Analytics
              </Typography>
              <Typography variant="body2" sx={styles.headerSubtitle}>
                <FiActivity size={16} />
                Real-time monitoring & insights
                <Chip 
                  label="Live" 
                  size="small"
                  sx={{ 
                    ml: 1,
                    background: alpha('#10b981', 0.1),
                    color: '#059669',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                  }}
                />
              </Typography>
            </Box>
          </Box>

          <Box sx={styles.headerStats}>
            <Box sx={styles.headerStatItem}>
              <FiCalendar size={14} color="#64748b" />
              <Typography variant="caption" sx={{ fontWeight: 500, color: '#475569' }}>
                {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem sx={{ bgcolor: '#e2e8f0' }} />
            <Box sx={styles.headerStatItem}>
              <FiUsers size={14} color="#64748b" />
              <Typography variant="caption" sx={{ fontWeight: 500, color: '#475569' }}>
                {robotCount || 0} Active Robots
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Stats Grid */}
        <Grid container spacing={3} sx={styles.statsGrid}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<FiShield size={24} />}
              value={robotCount}
              label="Total Robots"
              trend={null}
              loading={loadingRobots}
              accentColor="#6366f1"
              subValue={robotCount > 0 ? 'active' : null}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<FiZap size={24} />}
              value={stats.totalDisinfection.toLocaleString()}
              label="Total Disinfections"
              trend={stats.trendDisinfection}
              loading={loadingHistory}
              accentColor="#0d9488"
              subValue="cycles"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<FiClock size={24} />}
              value={`${stats.totalRobotRunHours}h`}
              label="Runtime"
              trend={null}
              loading={loadingHistory}
              accentColor="#0891b2"
              subValue={`avg ${stats.avgCycleTime}m/cycle`}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<MdOutlineCheckCircle size={24} />}
              value={`${stats.successRate}%`}
              label="Success Rate"
              trend={null}
              loading={loadingHistory}
              accentColor="#8b5cf6"
              subValue={`${Math.round(stats.totalDisinfection * stats.successRate / 100)} completed`}
            />
          </Grid>
        </Grid>

        {/* Mode: AutoDisinfection | Status: Completed & Partial */}
        <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <Typography variant="subtitle2" sx={{ color: '#64748b', fontWeight: 600, mr: 1 }}>
            Mode:
          </Typography>
          <Chip
            label={`AutoDisinfection ${stats.statusCounts.AutoDisinfection}`}
            size="small"
            sx={{ bgcolor: alpha('#0d9488', 0.12), color: '#0f766e', fontWeight: 600 }}
          />
          <Typography variant="subtitle2" sx={{ color: '#64748b', fontWeight: 600, ml: 2, mr: 1 }}>
            Status:
          </Typography>
          <Chip
            label={`Completed ${stats.statusCounts.completed}`}
            size="small"
            sx={{ bgcolor: alpha('#059669', 0.12), color: '#047857', fontWeight: 600 }}
          />
          <Chip
            label={`Partial ${stats.statusCounts.partial}`}
            size="small"
            sx={{ bgcolor: alpha('#f59e0b', 0.12), color: '#b45309', fontWeight: 600 }}
          />
        </Box>

        {/* Charts Row */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} lg={8}>
            <Card sx={styles.chartCard}>
              <CardContent sx={styles.chartCardContent}>
                <Box sx={styles.chartHeader}>
                  <Box>
                    <Typography variant="subtitle1" sx={styles.chartTitle}>
                      <FiBarChart2 size={20} color="#0d9488" />
                      Disinfection Trends
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {chartRange === 'Day' && stats.dailyData.length
                        ? `${stats.dailyData[0]?.date} – ${stats.dailyData[stats.dailyData.length - 1]?.date}`
                        : chartRange === 'Week' && stats.weeklyData.length
                        ? `${stats.weeklyData[0]?.date} – ${stats.weeklyData[stats.weeklyData.length - 1]?.date}`
                        : chartRange === 'Month' && stats.monthlyData.length
                        ? `${stats.monthlyData[0]?.date} – ${stats.monthlyData[stats.monthlyData.length - 1]?.date}`
                        : 'No data available'}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: 2,
                      ...styles.chartRangeToggle,
                    }}
                  >
                    <Button
                      size="small"
                      variant={chartRange === 'Day' ? 'contained' : 'outlined'}
                      onClick={() => setChartRange('Day')}
                    >
                      Day
                    </Button>
                    <Button
                      size="small"
                      variant={chartRange === 'Week' ? 'contained' : 'outlined'}
                      onClick={() => setChartRange('Week')}
                    >
                      Week
                    </Button>
                    <Button
                      size="small"
                      variant={chartRange === 'Month' ? 'contained' : 'outlined'}
                      onClick={() => setChartRange('Month')}
                    >
                      Month
                    </Button>
                  </Box>
                </Box>

                {error && (
                  <Paper sx={{ p: 2, mb: 2, bgcolor: alpha('#ef4444', 0.05), border: '1px solid', borderColor: alpha('#ef4444', 0.2) }}>
                    <Typography color="error" variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MdOutlineWarning size={18} />
                      {error}
                    </Typography>
                  </Paper>
                )}

                {loading ? (
                  <Box sx={{ height: 320 }}>
                    <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2, mb: 1 }} />
                    <Skeleton variant="text" width="40%" />
                  </Box>
                ) : chartData.length === 0 ? (
                  <Box sx={styles.chartEmpty}>
                    <FiActivity size={48} color="#94a3b8" style={{ marginBottom: 16, opacity: 0.5 }} />
                    <Typography variant="body1" sx={{ fontWeight: 500, color: '#64748b', mb: 0.5 }}>
                      No disinfection data yet
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      History will appear here after first disinfection cycle
                    </Typography>
                  </Box>
                ) : (
                  <ResponsiveContainer width="100%" height={320}>
                    <AreaChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                      <defs>
                        <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#0d9488" stopOpacity={0.25} />
                          <stop offset="95%" stopColor="#0d9488" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                      <XAxis
                        dataKey="date"
                        tick={{ fill: '#64748b', fontSize: 11, fontWeight: 500 }}
                        tickLine={false}
                        axisLine={{ stroke: '#e2e8f0' }}
                        tickFormatter={(v) => {
                          if (!v) return '';
                          if (chartRange === 'Month') return v.slice(5) || v;
                          if (chartRange === 'Week') return v.slice(5) || v;
                          return v.slice(5) || v;
                        }}
                      />
                      <YAxis
                        tick={{ fill: '#64748b', fontSize: 11, fontWeight: 500 }}
                        tickLine={false}
                        axisLine={{ stroke: '#e2e8f0' }}
                        allowDecimals={false}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Area
                        type="monotone"
                        dataKey="count"
                        stroke="#0d9488"
                        strokeWidth={2.5}
                        fill="url(#areaGradient)"
                        dot={false}
                        activeDot={{ r: 6, stroke: '#0d9488', strokeWidth: 2, fill: '#ffffff' }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Card
              sx={{
                ...styles.chartCard,
                ...(!loading && {
                  cursor: 'pointer',
                  transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 12px 28px -8px rgba(15, 23, 42, 0.12)',
                    transform: 'translateY(-2px)',
                  },
                }),
              }}
              onClick={() => !loading && setOverviewDialogOpen(true)}
              role={!loading ? 'button' : undefined}
              tabIndex={!loading ? 0 : undefined}
              onKeyDown={(e) => {
                if (loading) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setOverviewDialogOpen(true);
                }
              }}
              aria-label={
                !loading
                  ? 'Open enlarged overview chart'
                  : undefined
              }
            >
              <CardContent sx={styles.chartCardContent}>
                <Box sx={{ ...styles.chartHeader, mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle1" sx={styles.chartTitle}>
                      <FiCalendar size={20} color="#0d9488" />
                      {overviewRange === 'Week' && 'This Week Overview'}
                      {overviewRange === 'Month' && 'Month Overview'}
                      {overviewRange === 'Year' && 'Year Overview'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {overviewRange === 'Week' && 'Daily disinfection count'}
                      {overviewRange === 'Month' && 'All months since disinfection started'}
                      {overviewRange === 'Year' && 'All years since disinfection started'}
                      {!loading && (
                        <Box component="span" sx={{ display: 'block', mt: 0.5, color: '#0d9488', fontWeight: 600 }}>
                          Click to enlarge
                        </Box>
                      )}
                    </Typography>
                  </Box>
                  <Box
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: 2,
                      ...styles.chartRangeToggle,
                    }}
                  >
                    <Button
                      size="small"
                      variant={overviewRange === 'Week' ? 'contained' : 'outlined'}
                      onClick={() => setOverviewRange('Week')}
                    >
                      Week
                    </Button>
                    <Button
                      size="small"
                      variant={overviewRange === 'Month' ? 'contained' : 'outlined'}
                      onClick={() => setOverviewRange('Month')}
                    >
                      Month
                    </Button>
                    <Button
                      size="small"
                      variant={overviewRange === 'Year' ? 'contained' : 'outlined'}
                      onClick={() => setOverviewRange('Year')}
                    >
                      Year
                    </Button>
                  </Box>
                </Box>

                {loading ? (
                  <Box sx={{ height: 240 }}>
                    <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2, mb: 1 }} />
                    <Skeleton variant="text" width="60%" />
                  </Box>
                ) : (() => {
                  const overviewData = overviewRange === 'Week' ? stats.weekBarData : overviewRange === 'Month' ? stats.monthOverviewData : stats.yearOverviewData;
                  const dataKey = overviewRange === 'Week' ? 'day' : overviewRange === 'Month' ? 'month' : 'year';
                  const total = overviewData.reduce((sum, item) => sum + item.count, 0);
                  const count = overviewData.length || 1;
                  const average = Math.round(total / count);

                  return (
                    <>
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={overviewData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                          <XAxis
                            dataKey={dataKey}
                            tick={{ fill: '#64748b', fontSize: overviewRange === 'Year' ? 10 : 11, fontWeight: 500 }}
                            tickLine={false}
                            axisLine={{ stroke: '#e2e8f0' }}
                            interval={overviewRange === 'Year' ? 0 : 'preserveStartEnd'}
                          />
                          <YAxis
                            tick={{ fill: '#64748b', fontSize: 11, fontWeight: 500 }}
                            tickLine={false}
                            axisLine={false}
                            allowDecimals={false}
                          />
                          <Tooltip
                            content={({ active, payload }) => {
                              if (!active || !payload?.length) return null;
                              return (
                                <Paper elevation={0} sx={styles.chartTooltip}>
                                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                                    {payload[0]?.payload?.fullDate || payload[0]?.payload?.name}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: '#0d9488', fontWeight: 600 }}>
                                    {payload[0]?.value} disinfections
                                  </Typography>
                                </Paper>
                              );
                            }}
                          />
                          <Bar
                            dataKey="count"
                            fill="#0d9488"
                            radius={[6, 6, 0, 0]}
                            barSize={overviewRange === 'Year' ? 24 : 36}
                          >
                            <LabelList dataKey="count" position="top" style={{ fontSize: 11, fontWeight: 600, fill: '#0f172a' }} />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>

                      <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {overviewRange === 'Week' && 'Weekly Total'}
                            {overviewRange === 'Month' && 'All-Months Total'}
                            {overviewRange === 'Year' && 'All-Years Total'}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                            {total}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {overviewRange === 'Week' && 'Daily Average'}
                            {overviewRange === 'Month' && 'Monthly Average'}
                            {overviewRange === 'Year' && 'Yearly Average'}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                            {average}
                          </Typography>
                        </Box>
                        {overviewRange === 'Week' && stats.trendDisinfection != null && (
                          <Chip
                            label={`${stats.trendDisinfection >= 0 ? '+' : ''}${stats.trendDisinfection}% vs last week`}
                            size="small"
                            icon={stats.trendDisinfection >= 0 ? <FiTrendingUp /> : <FiTrendingDown />}
                            sx={{
                              bgcolor: stats.trendDisinfection >= 0 ? alpha('#10b981', 0.1) : alpha('#ef4444', 0.1),
                              color: stats.trendDisinfection >= 0 ? '#059669' : '#dc2626',
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </Box>
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Robot completion pie charts */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                ...styles.chartCard,
                ...(robotCompletedPieData.length > 0 && {
                  cursor: 'pointer',
                  transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 12px 28px -8px rgba(15, 23, 42, 0.12)',
                    transform: 'translateY(-2px)',
                  },
                }),
              }}
              onClick={() => robotCompletedPieData.length > 0 && setOverallCompletionDialogOpen(true)}
              role={robotCompletedPieData.length > 0 ? 'button' : undefined}
              tabIndex={robotCompletedPieData.length > 0 ? 0 : undefined}
              onKeyDown={(e) => {
                if (robotCompletedPieData.length === 0) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setOverallCompletionDialogOpen(true);
                }
              }}
              aria-label={
                robotCompletedPieData.length > 0
                  ? `Open enlarged ${DEFAULT_ROBOT_DISPLAY_NAME} overall completion chart`
                  : undefined
              }
            >
              <CardContent sx={styles.chartCardContent}>
                <Box sx={styles.chartHeader}>
                  <Box>
                    <Typography variant="subtitle1" sx={styles.chartTitle}>
                      <FiCpu size={20} color="#0d9488" />
                      {DEFAULT_ROBOT_DISPLAY_NAME} Overall Completion Rate
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      Share of completed disinfections per robot — count and % of total completed
                      {robotCompletedPieData.length > 0 && (
                        <Box component="span" sx={{ display: 'block', mt: 0.5, color: '#0d9488', fontWeight: 600 }}>
                          Click chart to enlarge
                        </Box>
                      )}
                    </Typography>
                  </Box>
                </Box>
                {loading ? (
                  <Box sx={{ height: 320 }}>
                    <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
                  </Box>
                ) : robotCompletedPieData.length === 0 ? (
                  <Box sx={styles.chartEmpty}>
                    <Typography variant="body2" color="text.secondary">
                      No completed disinfection records to chart.
                    </Typography>
                  </Box>
                ) : (
                  <ResponsiveContainer width="100%" height={320}>
                    <PieChart>
                      <Pie
                        data={robotCompletedPieData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="48%"
                        innerRadius={52}
                        outerRadius={100}
                        paddingAngle={2}
                        label={({ name, payload }) => `${name}: ${payload?.percentage ?? 0}%`}
                      >
                        {robotCompletedPieData.map((entry, index) => (
                          <Cell key={`cell-all-${entry.name}`} fill={PIE_COLORS[index % PIE_COLORS.length]} stroke="#fff" strokeWidth={1} />
                        ))}
                      </Pie>
                      <Tooltip content={<PieSliceTooltip />} />
                      <Legend
                        verticalAlign="bottom"
                        height={36}
                        formatter={(value) => {
                          const item = robotCompletedPieData.find((d) => d.name === value);
                          return item ? `${value} — ${item.count} (${item.percentage}%)` : value;
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                ...styles.chartCard,
                ...(periodRobotPieData.length > 0 && !loading && {
                  cursor: 'pointer',
                  transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 12px 28px -8px rgba(15, 23, 42, 0.12)',
                    transform: 'translateY(-2px)',
                  },
                }),
              }}
              onClick={() => periodRobotPieData.length > 0 && !loading && setPeriodPieDialogOpen(true)}
              role={periodRobotPieData.length > 0 && !loading ? 'button' : undefined}
              tabIndex={periodRobotPieData.length > 0 && !loading ? 0 : undefined}
              onKeyDown={(e) => {
                if (periodRobotPieData.length === 0 || loading) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setPeriodPieDialogOpen(true);
                }
              }}
              aria-label={
                periodRobotPieData.length > 0 && !loading
                  ? `Open enlarged ${DEFAULT_ROBOT_DISPLAY_NAME} period completion chart`
                  : undefined
              }
            >
              <CardContent sx={styles.chartCardContent}>
                <Box sx={{ ...styles.chartHeader, flexWrap: 'wrap', gap: 1, alignItems: 'flex-start' }}>
                  <Box sx={{ flex: '1 1 auto', minWidth: 200 }}>
                    <Typography variant="subtitle1" sx={styles.chartTitle}>
                      <FiCalendar size={20} color="#0d9488" />
                      {DEFAULT_ROBOT_DISPLAY_NAME} (day / month / year)
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {piePeriodKind === 'day' && 'Share of completed runs on the selected day'}
                      {piePeriodKind === 'month' && 'Share of completed runs in the selected calendar month'}
                      {piePeriodKind === 'year' && 'Share of completed runs in the selected calendar year'}
                      {periodRobotPieData.length > 0 && !loading && (
                        <Box component="span" sx={{ display: 'block', mt: 0.5, color: '#0d9488', fontWeight: 600 }}>
                          Click chart to enlarge
                        </Box>
                      )}
                    </Typography>
                  </Box>
                  <Box
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: 2,
                      ...styles.chartRangeToggle,
                    }}
                  >
                    <Button
                      size="small"
                      variant={piePeriodKind === 'day' ? 'contained' : 'outlined'}
                      onClick={() => setPiePeriodKind('day')}
                    >
                      Day
                    </Button>
                    <Button
                      size="small"
                      variant={piePeriodKind === 'month' ? 'contained' : 'outlined'}
                      onClick={() => setPiePeriodKind('month')}
                    >
                      Month
                    </Button>
                    <Button
                      size="small"
                      variant={piePeriodKind === 'year' ? 'contained' : 'outlined'}
                      onClick={() => setPiePeriodKind('year')}
                    >
                      Year
                    </Button>
                  </Box>
                </Box>
                <Box
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1, mt: 1, alignItems: 'center' }}
                >
                  {piePeriodKind === 'day' && (
                    <TextField
                      type="date"
                      size="small"
                      label="Date"
                      value={pieDay}
                      onChange={(e) => setPieDay(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        min: historyDateBounds.min ?? undefined,
                        max: historyDateBounds.max ?? undefined,
                      }}
                      sx={{ minWidth: 160 }}
                    />
                  )}
                  {piePeriodKind === 'month' && (
                    <TextField
                      type="month"
                      size="small"
                      label="Month"
                      value={pieMonth}
                      onChange={(e) => setPieMonth(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        min: historyDateBounds.minMonth ?? undefined,
                        max: historyDateBounds.maxMonth ?? undefined,
                      }}
                      sx={{ minWidth: 160 }}
                    />
                  )}
                  {piePeriodKind === 'year' && (
                    <TextField
                      select
                      size="small"
                      label="Year"
                      value={pieYear}
                      onChange={(e) => setPieYear(e.target.value)}
                      sx={{ minWidth: 120 }}
                    >
                      {pieYearOptions.map((y) => (
                        <MenuItem key={y} value={y}>
                          {y}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </Box>
                {loading ? (
                  <Box sx={{ height: 320 }}>
                    <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
                  </Box>
                ) : periodRobotPieData.length === 0 ? (
                  <Box sx={styles.chartEmpty}>
                    <Typography variant="body2" color="text.secondary">
                      {!historyDateBounds.min
                        ? 'No history data for this chart.'
                        : piePeriodKind === 'day'
                        ? `No completed disinfections on ${pieDay}. Try another date.`
                        : piePeriodKind === 'month'
                        ? `No completed disinfections in ${pieMonth}. Try another month.`
                        : `No completed disinfections in ${pieYear}. Try another year.`}
                    </Typography>
                  </Box>
                ) : (
                  <ResponsiveContainer width="100%" height={320}>
                    <PieChart>
                      <Pie
                        data={periodRobotPieData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="48%"
                        innerRadius={52}
                        outerRadius={100}
                        paddingAngle={2}
                        label={({ name, payload }) => `${name}: ${payload?.percentage ?? 0}%`}
                      >
                        {periodRobotPieData.map((entry, index) => (
                          <Cell key={`cell-period-${piePeriodKind}-${entry.name}`} fill={PIE_COLORS[index % PIE_COLORS.length]} stroke="#fff" strokeWidth={1} />
                        ))}
                      </Pie>
                      <Tooltip content={<PieSliceTooltip />} />
                      <Legend
                        verticalAlign="bottom"
                        height={36}
                        formatter={(value) => {
                          const item = periodRobotPieData.find((d) => d.name === value);
                          return item ? `${value} — ${item.count} (${item.percentage}%)` : value;
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Dialog
          open={overallCompletionDialogOpen}
          onClose={() => setOverallCompletionDialogOpen(false)}
          maxWidth="md"
          fullWidth
          scroll="body"
          PaperProps={{
            sx: {
              borderRadius: 3,
              maxWidth: 640,
              width: '100%',
              m: { xs: 2, sm: 3 },
            },
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              pr: 1,
              pt: 2.5,
              pb: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
              <FiCpu size={22} color="#0d9488" />
              <Typography component="span" variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                {DEFAULT_ROBOT_DISPLAY_NAME} Overall Completion Rate
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setOverallCompletionDialogOpen(false)}
              aria-label="Close"
              sx={{ color: '#64748b' }}
            >
              <FiX size={20} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pb: 3, pt: 0 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Share of completed disinfections per robot — count and % of total completed
            </Typography>
            {robotCompletedPieData.length > 0 && (
              <Box sx={{ width: '100%', minHeight: 400 }}>
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart>
                    <Pie
                      data={robotCompletedPieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="45%"
                      innerRadius={64}
                      outerRadius={128}
                      paddingAngle={2}
                      label={({ name, payload }) => `${name}: ${payload?.percentage ?? 0}%`}
                    >
                      {robotCompletedPieData.map((entry, index) => (
                        <Cell key={`cell-all-dialog-${entry.name}`} fill={PIE_COLORS[index % PIE_COLORS.length]} stroke="#fff" strokeWidth={1} />
                      ))}
                    </Pie>
                    <Tooltip content={<PieSliceTooltip />} />
                    <Legend
                      verticalAlign="bottom"
                      height={48}
                      wrapperStyle={{ paddingTop: 8 }}
                      formatter={(value) => {
                        const item = robotCompletedPieData.find((d) => d.name === value);
                        return item ? `${value} — ${item.count} (${item.percentage}%)` : value;
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            )}
          </DialogContent>
        </Dialog>

        <Dialog
          open={periodPieDialogOpen}
          onClose={() => setPeriodPieDialogOpen(false)}
          maxWidth="md"
          fullWidth
          scroll="body"
          PaperProps={{
            sx: {
              borderRadius: 3,
              maxWidth: 640,
              width: '100%',
              m: { xs: 2, sm: 3 },
            },
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              pr: 1,
              pt: 2.5,
              pb: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
              <FiCalendar size={22} color="#0d9488" />
              <Typography component="span" variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                {DEFAULT_ROBOT_DISPLAY_NAME} (day / month / year)
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setPeriodPieDialogOpen(false)}
              aria-label="Close"
              sx={{ color: '#64748b' }}
            >
              <FiX size={20} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pb: 3, pt: 0 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {piePeriodKind === 'day' && 'Share of completed runs on the selected day'}
              {piePeriodKind === 'month' && 'Share of completed runs in the selected calendar month'}
              {piePeriodKind === 'year' && 'Share of completed runs in the selected calendar year'}
            </Typography>
            {periodRobotPieData.length > 0 && (
              <Box sx={{ width: '100%', minHeight: 400 }}>
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart>
                    <Pie
                      data={periodRobotPieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="45%"
                      innerRadius={64}
                      outerRadius={128}
                      paddingAngle={2}
                      label={({ name, payload }) => `${name}: ${payload?.percentage ?? 0}%`}
                    >
                      {periodRobotPieData.map((entry, index) => (
                        <Cell
                          key={`cell-period-dialog-${entry.name}`}
                          fill={PIE_COLORS[index % PIE_COLORS.length]}
                          stroke="#fff"
                          strokeWidth={1}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<PieSliceTooltip />} />
                    <Legend
                      verticalAlign="bottom"
                      height={48}
                      wrapperStyle={{ paddingTop: 8 }}
                      formatter={(value) => {
                        const item = periodRobotPieData.find((d) => d.name === value);
                        return item ? `${value} — ${item.count} (${item.percentage}%)` : value;
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            )}
          </DialogContent>
        </Dialog>

        <Dialog
          open={overviewDialogOpen}
          onClose={() => setOverviewDialogOpen(false)}
          maxWidth="md"
          fullWidth
          scroll="body"
          PaperProps={{
            sx: {
              borderRadius: 3,
              maxWidth: 640,
              width: '100%',
              m: { xs: 2, sm: 3 },
            },
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              pr: 1,
              pt: 2.5,
              pb: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
              <FiCalendar size={22} color="#0d9488" />
              <Typography component="span" variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                {overviewRange === 'Week' && 'This Week Overview'}
                {overviewRange === 'Month' && 'Month Overview'}
                {overviewRange === 'Year' && 'Year Overview'}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setOverviewDialogOpen(false)}
              aria-label="Close"
              sx={{ color: '#64748b' }}
            >
              <FiX size={20} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pb: 3, pt: 0 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {overviewRange === 'Week' && 'Daily disinfection count'}
              {overviewRange === 'Month' && 'All months since disinfection started'}
              {overviewRange === 'Year' && 'All years since disinfection started'}
            </Typography>
            {(() => {
              const overviewData =
                overviewRange === 'Week'
                  ? stats.weekBarData
                  : overviewRange === 'Month'
                  ? stats.monthOverviewData
                  : stats.yearOverviewData;
              const dataKey = overviewRange === 'Week' ? 'day' : overviewRange === 'Month' ? 'month' : 'year';
              const total = overviewData.reduce((sum, item) => sum + item.count, 0);
              const count = overviewData.length || 1;
              const average = Math.round(total / count);
              return (
                <>
                  <ResponsiveContainer width="100%" height={340}>
                    <BarChart data={overviewData} margin={{ top: 12, right: 12, left: 8, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                      <XAxis
                        dataKey={dataKey}
                        tick={{ fill: '#64748b', fontSize: overviewRange === 'Year' ? 10 : 11, fontWeight: 500 }}
                        tickLine={false}
                        axisLine={{ stroke: '#e2e8f0' }}
                        interval={overviewRange === 'Year' ? 0 : 'preserveStartEnd'}
                      />
                      <YAxis
                        tick={{ fill: '#64748b', fontSize: 11, fontWeight: 500 }}
                        tickLine={false}
                        axisLine={false}
                        allowDecimals={false}
                      />
                      <Tooltip
                        content={({ active, payload }) => {
                          if (!active || !payload?.length) return null;
                          return (
                            <Paper elevation={0} sx={styles.chartTooltip}>
                              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                                {payload[0]?.payload?.fullDate || payload[0]?.payload?.name}
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#0d9488', fontWeight: 600 }}>
                                {payload[0]?.value} disinfections
                              </Typography>
                            </Paper>
                          );
                        }}
                      />
                      <Bar
                        dataKey="count"
                        fill="#0d9488"
                        radius={[6, 6, 0, 0]}
                        barSize={overviewRange === 'Year' ? 28 : 40}
                      >
                        <LabelList dataKey="count" position="top" style={{ fontSize: 12, fontWeight: 600, fill: '#0f172a' }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {overviewRange === 'Week' && 'Weekly Total'}
                        {overviewRange === 'Month' && 'All-Months Total'}
                        {overviewRange === 'Year' && 'All-Years Total'}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                        {total}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {overviewRange === 'Week' && 'Daily Average'}
                        {overviewRange === 'Month' && 'Monthly Average'}
                        {overviewRange === 'Year' && 'Yearly Average'}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                        {average}
                      </Typography>
                    </Box>
                    {overviewRange === 'Week' && stats.trendDisinfection != null && (
                      <Chip
                        label={`${stats.trendDisinfection >= 0 ? '+' : ''}${stats.trendDisinfection}% vs last week`}
                        size="small"
                        icon={stats.trendDisinfection >= 0 ? <FiTrendingUp /> : <FiTrendingDown />}
                        sx={{
                          bgcolor: stats.trendDisinfection >= 0 ? alpha('#10b981', 0.1) : alpha('#ef4444', 0.1),
                          color: stats.trendDisinfection >= 0 ? '#059669' : '#dc2626',
                          fontWeight: 600,
                        }}
                      />
                    )}
                  </Box>
                </>
              );
            })()}
          </DialogContent>
        </Dialog>

        {/* Bottom Summary Cards */}
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                ...styles.summaryCard,
                cursor: 'pointer',
                '&:focus-visible': { outline: `2px solid ${alpha('#6366f1', 0.6)}`, outlineOffset: 2 },
              }}
              onClick={() => navigate('/analytics/robots-list')}
              role="link"
              tabIndex={0}
              aria-label="Open Analytics Report dashboard"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate('/analytics/robots-list');
                }
              }}
            >
              <CardContent sx={styles.summaryCardContent}>
                <Box sx={styles.summaryText}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a', mb: 0.5 }}>
                    Analytics Report
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <FiBarChart2 size={14} color="#6366f1" />
                    Analytics dashboard, robot history & operational metrics
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 1.5,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      fontWeight: 600,
                      color: '#6366f1',
                    }}
                  >
                    Open dashboard
                    <FiArrowRight size={14} aria-hidden />
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.18) 0%, rgba(13, 148, 136, 0.14) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#6366f1',
                    flexShrink: 0,
                  }}
                  aria-hidden
                >
                  <FiBarChart2 size={36} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={styles.summaryCard}>
              <CardContent sx={styles.summaryCardContent}>
                <Box sx={styles.summaryText}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a', mb: 0.5 }}>
                    UV System Performance
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <FiZap size={14} color="#0d9488" />
                    {stats.totalLightOnHours}h total UV time
                  </Typography>
                  <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                    <Chip size="small" label={`${stats.successRate}% success`} sx={{ bgcolor: alpha('#0d9488', 0.1), color: '#0d9488' }} />
                    <Chip size="small" label={`${stats.avgCycleTime}m avg cycle`} sx={{ bgcolor: alpha('#6366f1', 0.1), color: '#6366f1' }} />
                  </Box>
                </Box>
                <img src="/Images/Robot_1.png" alt="Sportbot Lite" style={styles.summaryImage} />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={styles.summaryCard}>
              <CardContent sx={styles.summaryCardContent}>
                <Box sx={styles.summaryText}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a', mb: 0.5 }}>
                    Fleet Overview
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <FiCpu size={14} color="#6366f1" />
                    {robotCount || 0} robots · {stats.totalRobotRunHours}h runtime
                  </Typography>
                  <Box sx={{ mt: 1.5 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={75} 
                      sx={{ 
                        height: 6, 
                        borderRadius: 3,
                        bgcolor: alpha('#6366f1', 0.1),
                        '& .MuiLinearProgress-bar': {
                          bgcolor: '#6366f1',
                          borderRadius: 3,
                        }
                      }} 
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      75% fleet utilization
                    </Typography>
                  </Box>
                </Box>
                <img src="/Spotless.png" alt="Spotless" style={styles.summaryImage} />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={styles.summaryCard}>
              <CardContent sx={styles.summaryCardContent}>
                <Box sx={styles.summaryText}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a', mb: 0.5 }}>
                    Top Locations
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {stats.locationData && stats.locationData.slice(0, 3).map((loc, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          <FiMapPin size={10} style={{ marginRight: 4 }} />
                          {loc.name}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#0d9488' }}>
                          {loc.value} cycles
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    <FiBarChart2 size={10} style={{ marginRight: 4 }} />
                    {stats.totalDisinfection} total cycles
                  </Typography>
                </Box>
                <img src="/Images/Demo_img_1.png" alt="Analytics" style={styles.summaryImage} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}